import { UIElement } from '../ui-element';
import { UIField } from '../field/ui-field';
import { getAttributes } from '../../global/ui-helpers';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import { Children } from '../../decorators/child.decorator';
import type {
    FieldConfig,
    FieldControl,
    FieldPatchFn,
    ControlPatchFn,
    UIFieldInstance,
} from './ui-fieldset.type';
import type {
    ElementConfig,
    AttributeValue,
    ObservedAttributes,
} from '../ui-element.type';
import type { NullOr } from '../../global/utils.type';
import styles from './ui-fieldset.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIFieldset
 * @element ui-fieldset
 * @classdesc Represents a class for <code>ui-fieldset</code> element.
 * @property {boolean} [autosort] {@attr autosort} Auto-sorting of fields is enabled/disabled.
 * @property {UIField} fields Gets all fields of UIFieldset.
 * @property {object} listeners List of all listeners.
 * @deprecated This element is used only in ibank payment form.
 * @example
 * <ui-fieldset autosort="true"></ui-fieldset>
 */
@Component({ tag: 'ui-fieldset', styles })
export class UIFieldset extends UIElement {
    static readonly OBESERVABLE_ELEMENTS: ObservedAttributes = [
        'input[name]',
        'select[name]',
        'textarea[name]',
    ];

    @Attribute(Boolean) autosort?: boolean;

    @Children('ui-field') fields!: Array<UIField>;

    listeners?: Record<string, Array<EventListener>>;

    static sortPredicate(fieldA: FieldConfig, fieldB: FieldConfig): number {
        return fieldA.index! - fieldB.index!;
    }

    /**
     * @param {Array<FieldConfig>} fieldSet
     * @returns {Record<string, FieldConfig>}
     */
    static toModel(fieldSet: Array<FieldConfig>): Record<string, FieldConfig> {
        return fieldSet.reduce<Record<string, FieldConfig>>(function (
            res,
            oldField
        ) {
            res[oldField.id] = oldField;
            return res;
        }, {});
    }

    /**
     * @param {Record<string, FieldConfig>} fieldSet
     * @returns {Array<FieldConfig>}
     */
    static toArray(fieldSet: Record<string, FieldConfig>): Array<FieldConfig> {
        return Object.keys(fieldSet).map(function (key) {
            return fieldSet[key];
        });
    }

    /**
     * Convert HTMLElement node to control config.
     * @param {HTMLElement} node
     * @param {string | null} value
     * @returns {Partial<FieldControl>}
     * @deprecated
     */
    static readControlConfig(
        node: HTMLElement,
        value: string | null
    ): Partial<FieldControl> {
        const dataControl = node;
        let target: HTMLElement = node;

        while (node.parentElement?.localName !== 'ui-field') {
            node = node.parentElement as HTMLElement;
            if (
                [
                    'input',
                    'select',
                    'textarea',
                    'ui-dropdown',
                    'ui-slider',
                    'ui-datepicker',
                ].indexOf(node.localName) > -1
            ) {
                target = node;
            }
        }

        if (!target) {
            return {};
        }

        const result: Partial<FieldControl> = {};

        const tagName = target.localName;
        switch (tagName) {
            case 'input':
            case 'select':
            case 'textarea':
                result.type = target.localName;
                break;
            case 'ui-slider':
                result.type = 'slider';
                result.config = {
                    units: target.getAttribute('units'),
                    class: target.getAttribute('class'),
                };
                break;
            case 'ui-dropdown':
                result.type = 'dropdown';
                result.config = {
                    view: target.getAttribute('layout'),
                    maxlength: Number(target.getAttribute('maxlength')) || null,
                };
                break;
            case 'ui-datepicker':
                result.type = 'date';
                break;
        }

        const attributes = getAttributes(dataControl);

        if (Object.keys(attributes).length > 0) {
            result.attributes = attributes;
        }

        if (tagName === 'select' || tagName === 'ui-dropdown') {
            result.options = Array.from(
                target.querySelectorAll<HTMLOptionElement>('option')
            ).map(function (opt) {
                const optResult: Record<string, unknown> = {
                    value: opt.getAttribute('value'),
                    content: opt.innerHTML,
                };

                if (Object.keys(opt.dataset).length > 0) {
                    optResult.data = UIElement.mergeAttributes({}, opt.dataset);
                }
                return optResult;
            });
        }

        if (value !== null && value !== '') {
            result.value = value;
        }

        return result;
    }

    /**
     * Convert ui-field node to config.
     * @param {UIField | HTMLElement} field
     * @returns {FieldConfig}
     * @deprecated
     */
    static readFieldConfig(field: UIField | HTMLElement): FieldConfig {
        let id = field.getAttribute('key');
        let control;
        const data = (field as UIField).getData();
        const controls = [].filter.call(
            field.querySelectorAll('[name]'),
            function (node: HTMLElement) {
                if (!id) {
                    id = node.getAttribute('name');
                }
                if (node.getAttribute('name') === id) {
                    control = UIFieldset.readControlConfig(node, data[id!]);
                    return false;
                }
                return true;
            }
        );

        const result: FieldConfig = {
            id: id!,
            label: (field as UIField & UIFieldInstance).label!,
            visible: !field.classList.contains('-hidden'),
            index: Number(field.dataset.index) || null!,
        };

        if (control) {
            result.control = control;
        }

        if (controls.length > 0) {
            result.controls = (controls as Array<HTMLElement>).reduce(function (
                res: Record<string, FieldControl>,
                node: HTMLElement
            ) {
                const key = node.getAttribute('name');
                res[key!] = UIFieldset.readControlConfig(
                    node,
                    data[key!]
                ) as FieldControl;
                return res;
            }, {});
        }

        return result;
    }

    /**
     * Convert data config to element config.
     * @param {FieldConfig} config
     * @returns {UIField | HTMLElement}
     */
    static toFieldElement(config: FieldConfig): NullOr<HTMLElement> {
        return UIElement.prototype.createElement.call(
            UIElement.prototype,
            UIField.fromFieldConfig(config)
        );
    }

    /**
     * @param {string} name
     * @param {FieldControl} oldControl
     * @param {FieldControl} newControl
     * @returns {Function | undefined}
     * @deprecated
     */
    static diffSelectOptions(
        name: string,
        oldControl: FieldControl,
        newControl: FieldControl
    ): FieldPatchFn {
        return function (root: UIFieldset, node?: HTMLElement): void {
            // Atm we have just simply rebuild all options
            const control = node?.querySelector<HTMLSelectElement>(
                'select[name=' + name + ']'
            );
            if (!control) {
                return;
            }
            root.detachChildNodes.call(control);
            root.insertElements.call(
                control,
                newControl.options?.map(function (option) {
                    return {
                        tagName: 'option',
                        attributes: {
                            value: option.value,
                            selected: option.value === newControl.value,
                        },
                        children: [option.content || ''],
                    };
                }) as Array<ElementConfig>
            );
        };
    }

    /**
     * @param {FieldConfig} oldField
     * @param {FieldConfig} newField
     * @returns {Function | undefined}
     * @deprecated
     */
    static diffHints(
        oldField: FieldConfig,
        newField: FieldConfig
    ): FieldPatchFn {
        // console.log('Rebuild hint list');
        return function (
            _root: UIFieldset,
            node?: HTMLElement | UIField
        ): void {
            // Atm we have just simply rebuild all hints
            (node as UIField)?.removeHints();
            newField?.hints?.forEach(function (hint) {
                (node as UIField).addHint(hint);
            });
        };
    }

    /**
     * @param {string} name
     * @param {FieldConfig} oldField
     * @param {FieldConfig} newField
     * @returns {Function | undefined}
     * @deprecated
     */
    static diffFieldProperties(
        name: string,
        oldField: FieldConfig,
        newField: FieldConfig
    ): FieldPatchFn | undefined {
        if (
            oldField[name as keyof FieldConfig] ===
                newField[name as keyof FieldConfig] ||
            !(name in newField)
        ) {
            return;
        }

        switch (name) {
            case 'index':
                // console.log('Change index for field');
                return function (_root: UIFieldset, node?: HTMLElement): void {
                    node?.setAttribute('data-index', String(newField.index));
                };
            case 'label':
                // console.log('Change label for field');
                return function (_root: UIFieldset, node?: HTMLElement): void {
                    node?.setAttribute('label', newField.label);
                };
            case 'visible':
                // console.log('Change visibility for field');
                return function (_root: UIFieldset, node?: HTMLElement): void {
                    const uiNode = node as UIFieldInstance | undefined;
                    if (newField.visible) {
                        uiNode?.show?.();
                    } else {
                        uiNode?.hide?.();
                    }
                };
            case 'hints':
                // console.log('Change hints for field');
                return UIFieldset.diffHints(oldField, newField);
            default:
                return;
        }
    }

    /**
     * @param {string} name
     * @param {FieldControl} oldControl
     * @param {FieldControl} newControl
     * @returns {FieldPatchFn}
     * @deprecated
     */
    static diffControl(
        name: string,
        oldControl: FieldControl,
        newControl: FieldControl
    ): FieldPatchFn {
        if ('type' in newControl && newControl.type !== oldControl.type) {
            // console.log('Type is different, rebuild the whole control');
            return function (root: UIFieldset, node?: HTMLElement): void {
                if (!node) {
                    return;
                }
                const uiNode = node as UIFieldInstance;
                const oldData = uiNode.getData ? uiNode.getData() : {};
                const oldNodeEl = node.querySelector('[name=' + name + ']');
                const mergedAttrs = UIElement.mergeAttributes(
                    newControl.attributes!,
                    { id: name, name: name }
                );
                if (mergedAttrs) {
                    (newControl as Record<string, unknown>)['attributes'] =
                        mergedAttrs;
                }
                const controlConfig =
                    UIField.buildControlFromConfig(newControl);
                if (!controlConfig) {
                    return;
                }

                const controlConfigs = Array.isArray(controlConfig)
                    ? controlConfig.filter(Boolean)
                    : [controlConfig];

                if (!oldNodeEl?.parentElement || controlConfigs.length === 0) {
                    return;
                }

                const newNodeElements = controlConfigs.map((config) =>
                    root.createElement(config as ElementConfig)
                );

                newNodeElements.forEach((newNodeEl) => {
                    oldNodeEl.parentElement!.insertBefore(newNodeEl, oldNodeEl);
                });
                oldNodeEl.parentElement.removeChild(oldNodeEl);
                if (!('value' in newControl) && name in oldData) {
                    // Restore previous value if new value is not presented
                    const newData: Record<string, string> = {};
                    newData[name] = oldData[name];
                    uiNode?.setData?.(newData);
                }
            };
        }

        const controlPatches: Array<ControlPatchFn> = [];

        if ('attributes' in newControl) {
            // console.log('Apply new attributes set', newControl.attributes);
            controlPatches.push(function (
                root: UIFieldset,
                _field: HTMLElement,
                control: HTMLElement
            ): void {
                // console.log('*****', control, '->', newControl.attributes);
                root.setAttributes.call(
                    control,
                    newControl.attributes as Record<string, AttributeValue>
                );
            });
        }

        if ('options' in newControl) {
            // console.log('Apply new options set');
            controlPatches.push(function (
                root: UIFieldset,
                field: HTMLElement,
                _control: HTMLElement
            ): void {
                const patch = UIFieldset.diffSelectOptions(
                    name,
                    oldControl,
                    newControl
                );
                patch(root, field);
            });
        }

        if ('value' in newControl && newControl.value !== oldControl.value) {
            controlPatches.push(function (
                _root: UIFieldset,
                field: HTMLElement,
                _control: HTMLElement
            ): void {
                const uiField = field as UIFieldInstance;
                const data: Record<string, string> = {};
                data[name] = newControl.value || '';
                uiField.setData?.(data);
            });
        }

        if (controlPatches.length > 0) {
            // console.log('Will apply control patches');
            return function (root: UIFieldset, node?: HTMLElement): void {
                // console.log('DO apply control patches', controlPatches.length);
                if (!node) {
                    return;
                }
                const ctrl = node.querySelector<HTMLElement>(
                    '[name=' + name + ']'
                );
                if (!ctrl) {
                    return;
                }
                controlPatches.forEach(function (patch) {
                    patch(root, node, ctrl);
                });
            };
        }

        // console.log('No changes for controls');
        return function (): void {
            // do nothing
        };
    }

    /**
     * @param {FieldConfig} oldField
     * @param {FieldConfig} newField
     * @returns {FieldPatchFn}
     * @deprecated
     */
    static diffField(
        oldField: FieldConfig,
        newField: FieldConfig | undefined
    ): FieldPatchFn {
        // console.log('Compare', oldField, newField);
        if (!newField) {
            // console.log('Remove old field');
            return function (root: UIFieldset): void {
                const node = root.querySelector('[key=' + oldField.id + ']');
                if (!node) {
                    return;
                }
                node.parentElement?.removeChild(node);
            };
        }

        const fieldPatches: Array<FieldPatchFn> = [];

        const propertyPatches = (
            ['visible', 'index', 'label', 'hints'] as Array<keyof FieldConfig>
        )
            .map(function (fieldName) {
                return UIFieldset.diffFieldProperties(
                    fieldName,
                    oldField,
                    newField
                );
            })
            .filter(
                (patch): patch is FieldPatchFn => typeof patch === 'function'
            );

        if (propertyPatches.length) {
            // console.log('Apply field patches');
            fieldPatches.push(function (
                root: UIFieldset,
                node?: HTMLElement
            ): void {
                propertyPatches.forEach(function (patch) {
                    patch(root, node);
                });
            });
        }

        const controlPatches: Array<FieldPatchFn> = [];

        if ('control' in newField && newField.control && oldField.control) {
            controlPatches.push(
                UIFieldset.diffControl(
                    oldField.id,
                    oldField.control,
                    newField.control
                )
            );
        }

        if (newField.controls) {
            Object.keys(newField.controls).forEach(function (key) {
                if (newField.controls && oldField.controls) {
                    controlPatches.push(
                        UIFieldset.diffControl(
                            key,
                            oldField.controls[key],
                            newField.controls[key]
                        )
                    );
                }
            });
        }

        if (controlPatches.length) {
            fieldPatches.push(function (
                root: UIFieldset,
                node?: HTMLElement
            ): void {
                controlPatches.forEach(function (patch) {
                    patch(root, node);
                });
            });
        }

        if (fieldPatches.length) {
            return function (root: UIFieldset): void {
                const node = root.querySelector<HTMLElement>(
                    '[key=' + oldField.id + ']'
                );
                if (!node) {
                    return;
                }
                fieldPatches.forEach(function (patch) {
                    patch(root, node);
                });
            };
        }

        // console.log('DO nothing');
        return function (): void {
            // do nothing
        };
    }

    /**
     * @param {Array<FieldConfig>} oldFieldSet
     * @param {Array<FieldConfig> | Record<string, FieldConfig>} newFieldSet
     * @returns {FieldPatchFn}
     * @deprecated
     */
    static diffFieldSet(
        oldFieldSet: Array<FieldConfig>,
        newFieldSet: Array<FieldConfig> | Record<string, FieldConfig>
    ): FieldPatchFn {
        const oldFieldsMap = UIFieldset.toModel(oldFieldSet);
        const newFieldsMap = Array.isArray(newFieldSet)
            ? UIFieldset.toModel(newFieldSet)
            : newFieldSet;

        const newFieldsArray = Array.isArray(newFieldSet)
            ? newFieldSet
            : UIFieldset.toArray(newFieldSet);

        const patches: Array<FieldPatchFn> = [];
        oldFieldSet.sort(UIFieldset.sortPredicate).forEach(function (oldField) {
            patches.push(
                UIFieldset.diffField(oldField, newFieldsMap[oldField.id])
            );
        });

        const additionalFields = newFieldsArray
            .sort(UIFieldset.sortPredicate)
            .filter(function (newField) {
                return !oldFieldsMap[newField.id];
            });

        if (additionalFields.length) {
            // console.log('Add new', additionalFields.length, 'fields');
            patches.push(function (root: UIFieldset): void {
                root.insertElements(
                    additionalFields.map(function (additionalField) {
                        const newNode = root.createElement(
                            UIField.fromFieldConfig(additionalField)
                        );
                        if (
                            'checkErrors' in newNode &&
                            typeof newNode.checkErrors === 'function'
                        ) {
                            newNode.checkErrors();
                        }
                        return newNode;
                    })
                );
            });
        }
        return function (root: UIFieldset): void {
            patches.forEach(function (patch) {
                patch(root);
            });
        };
    }

    /**
     * @returns {Record<string, FieldConfig>}
     */
    getModelData(): Record<string, FieldConfig> {
        return UIFieldset.toModel(this.getFieldConfigs());
    }

    /**
     * @param {string} key
     * @returns {HTMLElement | null}
     */
    getField(key: string): NullOr<HTMLElement> {
        return this.querySelector('ui-field[key=' + key + ']');
    }

    /**
     * @returns {Array<FieldConfig>}
     */
    getFieldConfigs(): Array<FieldConfig> {
        return Array.from(this.fields).map(UIFieldset.readFieldConfig);
    }

    /**
     * @param {Array<FieldConfig> | Record<string, FieldConfig>} newFieldSet
     */
    updateFieldSet(
        newFieldSet: Array<FieldConfig> | Record<string, FieldConfig>
    ): void {
        const newFieldsArray = Array.isArray(newFieldSet)
            ? newFieldSet
            : UIFieldset.toArray(newFieldSet);

        const newChildren = newFieldsArray
            .filter(function (config) {
                return !!config;
            })
            .sort(UIFieldset.sortPredicate);

        this.rebuildChildren(newChildren as Array<ElementConfig>);
        this.onFieldSetUpdate();
    }

    /**
     * Updates a field in the fieldset.
     * @param {FieldConfig} newField
     * @deprecated
     */
    updateField(newField: FieldConfig): void {
        const target = this.getField(newField.id);
        if (!target) {
            const newNode = this.createElement(
                UIField.fromFieldConfig(newField)
            );
            if (newNode) {
                this.appendChild(newNode);
            }
        } else {
            const patch = UIFieldset.diffField(
                UIFieldset.readFieldConfig(target),
                newField
            );
            patch(this, target);
        }
        this.onFieldSetUpdate();
    }

    /**
     * Sort fields based by index.
     */
    sortFieldSet(): void {
        if (!this.autosort) {
            return;
        }
        const configs = this.getFieldConfigs()
            .sort(UIFieldset.sortPredicate)
            .reverse();
        if (configs.length < 2) {
            return;
        }

        for (let i = 1; i < configs.length; i++) {
            const current = configs[i];
            const previous = configs[i - 1];

            this.insertBefore(
                this.getField(current.id)!,
                this.getField(previous.id)
            );
        }
    }

    /**
     * Sets fields to this current field set.
     * @param {Array<FieldConfig>} fields
     */
    setFields(fields: Array<FieldConfig>): void {
        this.insertElements(
            fields.sort(UIFieldset.sortPredicate).map(UIField.fromFieldConfig)
        );
    }

    /**
     * Lyfecycle hook called whenever fielset is updated.
     */
    onFieldSetUpdate(): void {
        this.sortFieldSet();

        // Resubscribe listeners
        if (!this.listeners) {
            return;
        }
        Object.keys(this.listeners).forEach((eventName) => {
            this.listeners![eventName].forEach((handler) => {
                this.unsubscribe(
                    UIFieldset.OBESERVABLE_ELEMENTS.join(','),
                    eventName,
                    handler
                );
                this.subscribe(
                    UIFieldset.OBESERVABLE_ELEMENTS.join(','),
                    eventName,
                    handler
                );
            });
        });
    }

    /**
     * Add event handler to controls.
     * @param {string} eventName
     * @param {EventListener} callback
     * @returns {UIFieldset}
     */
    addControlEventListener(eventName: string, callback: EventListener): this {
        if (!this.listeners) {
            this.listeners = {};
        }
        if (!this.listeners[eventName]) {
            this.listeners[eventName] = [];
        }
        this.listeners[eventName].push(callback);
        this.subscribe(
            UIFieldset.OBESERVABLE_ELEMENTS.join(','),
            eventName,
            callback
        );
        return this;
    }

    /**
     * Smoothly remove node from DOM tree
     * @returns {Promise<void>}
     */
    smoothRemove(): Promise<void> {
        return UIElement.runSmoothRemove(
            this,
            { '-fade-out': true, '-slide-out': true },
            400
        );
    }

    /**
     * Smoothly append the NODE to target element
     * @param {HTMLElement} target
     * @returns {Promise<void>}
     */
    smoothAppend(target: HTMLElement): Promise<void | HTMLElement> {
        return UIElement.runSmoothAppend(
            this,
            target,
            { '-fade-in': true, '-slide-in': true },
            400
        );
    }

    /**
     * @inheritDoc
     */
    override render(): void {}
}
